import { useState, useEffect, useCallback } from "react";
import { cn } from "@/utils/cn";

const GAME_PASSWORD = "112358";
export const GAME_SESSION_KEY = "semog_game_v1_unlocked";

export function isGameUnlocked(): boolean {
  return sessionStorage.getItem(GAME_SESSION_KEY) === "1";
}

interface GamePasswordModalProps {
  open: boolean;
  onSuccess: () => void;
  onClose: () => void;
}

export function GamePasswordModal({ open, onSuccess, onClose }: GamePasswordModalProps) {
  const [pin, setPin] = useState("");
  const [status, setStatus] = useState<"idle" | "error" | "success">("idle");

  const handleDigit = useCallback(
    (d: string) => {
      if (status !== "idle" || pin.length >= 6) return;
      const next = pin + d;
      setPin(next);

      if (next.length === 6) {
        if (next === GAME_PASSWORD) {
          setStatus("success");
          sessionStorage.setItem(GAME_SESSION_KEY, "1");
          setTimeout(() => {
            onSuccess();
            setPin("");
            setStatus("idle");
          }, 700);
        } else {
          setStatus("error");
          setTimeout(() => {
            setPin("");
            setStatus("idle");
          }, 700);
        }
      }
    },
    [pin, status, onSuccess]
  );

  const handleBack = useCallback(() => {
    if (status !== "idle") return;
    setPin((p) => p.slice(0, -1));
  }, [status]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (/^[0-9]$/.test(e.key)) handleDigit(e.key);
      if (e.key === "Backspace") handleBack();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, handleDigit, handleBack]);

  useEffect(() => {
    if (!open) {
      setPin("");
      setStatus("idle");
    }
  }, [open]);

  if (!open) return null;

  const borderColor =
    status === "success"
      ? "border-emerald-400 shadow-[0_0_24px_rgba(52,211,153,0.4)]"
      : status === "error"
        ? "border-red-500 shadow-[0_0_24px_rgba(239,68,68,0.4)]"
        : "border-emerald-800 shadow-[0_0_24px_rgba(52,211,153,0.1)]";

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm">
      <div
        className={cn(
          "bg-gray-950 border-2 p-7 w-72 select-none transition-all duration-200",
          borderColor
        )}
        style={{ fontFamily: "'Press Start 2P', monospace", imageRendering: "pixelated" }}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-3xl mb-3">🎮</div>
          <div
            className={cn(
              "text-[9px] tracking-widest uppercase transition-colors",
              status === "success" ? "text-emerald-400" : status === "error" ? "text-red-400" : "text-emerald-500"
            )}
          >
            {status === "success" ? "ACESSO LIBERADO!" : status === "error" ? "SENHA INCORRETA" : "ACESSO SECRETO"}
          </div>
        </div>

        {/* PIN dots */}
        <div className="flex justify-center gap-3 mb-7">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                "w-3.5 h-3.5 border-2 transition-all duration-150",
                i < pin.length
                  ? status === "success"
                    ? "bg-emerald-400 border-emerald-400"
                    : status === "error"
                      ? "bg-red-500 border-red-500"
                      : "bg-white border-white"
                  : "bg-transparent border-gray-700"
              )}
            />
          ))}
        </div>

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-1.5">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
            <button
              key={d}
              onClick={() => handleDigit(d)}
              className="h-11 bg-gray-900 hover:bg-gray-800 active:bg-gray-700 text-white text-base border border-gray-800 hover:border-emerald-700 transition-colors"
              style={{ fontFamily: "'Press Start 2P', monospace" }}
            >
              {d}
            </button>
          ))}
          <div />
          <button
            onClick={() => handleDigit("0")}
            className="h-11 bg-gray-900 hover:bg-gray-800 active:bg-gray-700 text-white text-base border border-gray-800 hover:border-emerald-700 transition-colors"
            style={{ fontFamily: "'Press Start 2P', monospace" }}
          >
            0
          </button>
          <button
            onClick={handleBack}
            className="h-11 bg-gray-900 hover:bg-gray-800 active:bg-gray-700 text-gray-400 hover:text-red-400 text-base border border-gray-800 hover:border-red-700 transition-colors"
            style={{ fontFamily: "'Press Start 2P', monospace" }}
          >
            ⌫
          </button>
        </div>

        <button
          onClick={onClose}
          className="mt-5 w-full text-gray-700 hover:text-gray-500 text-[8px] transition-colors"
          style={{ fontFamily: "'Press Start 2P', monospace" }}
        >
          CANCELAR
        </button>
      </div>
    </div>
  );
}
